import {
  Box,
  Button,
  debounce,
  IconButton,
  List,
  ListItem,
  Skeleton,
  Stack,
  StackProps,
  Tooltip,
  Typography,
} from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import {
  Assessment,
  AssessmentMethodType,
  AssessmentWithEntries,
  GetMarkbookSortBy,
  MarkbookAssessmentEntry,
  MarkbookCategory,
} from '@schooly/api';
import { AvatarAuth } from '@schooly/components/avatar-auth';
import { DataGridSortableCell, DataGridSortableCellProps } from '@schooly/components/data-grid';
import {
  CloseIcon,
  CompareIcon,
  Counter,
  EyeIcon,
  Loading,
  NewTabIcon,
  SimpleButton,
  TagSimple,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { getUserFullName } from '@schooly/utils/user-helpers';
import { FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, LinkProps } from 'react-router-dom';

import { PersonCardBasicProps } from '../../../../components/common/PersonCard/PersonCardBasic';
import { assessmentMethodTypes } from './assessmentEntryUpdates';
import { ASSESSMENT_CSS_CLASSES, useMarkbookForGroupStyling } from './assessmentStyling';
import { MarkbookAssessmentInfo } from './MarkbookAssessmentInfo';
import { MarkbookSubject } from './types';

export interface MarkbookForGroupHeaderCellProps {
  assessment: AssessmentWithEntries;
  onMouseOver?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onMouseLeave?: () => void;
}

export const MarkbookForGroupHeaderCell: FC<MarkbookForGroupHeaderCellProps> = ({
  assessment,
  onMouseOver,
  onMouseLeave,
}) => {
  return (
    <Stack
      justifyContent="center"
      sx={(theme) => ({
        width: '100%',
        height: '100%',
        px: theme.spacing(1),
        color: theme.palette.text.secondary,
      })}
      onMouseOver={onMouseOver}
      onMouseLeave={onMouseLeave}
    >
      <MarkbookAssessmentInfo assessment={assessment} />
    </Stack>
  );
};

export type MarkbookForGroupsStudentsHeaderCellProps = {
  kind: 'students';
  count: number;
  textId: string;
};

export const MarkbookForGroupsStudentsHeaderCell: FC<MarkbookForGroupsStudentsHeaderCellProps> = ({
  count,
  textId,
}) => {
  const { formatMessage } = useIntl();

  return (
    <Stack direction="row" alignItems="center" pt={1} pl={1}>
      <Typography component="span" variant="h2" color="text.primary">
        {formatMessage({ id: textId })}
      </Typography>
      <Counter className="counter" sx={{ minWidth: 20, textAlign: 'center', py: 0 }}>
        {count}
      </Counter>
    </Stack>
  );
};
export const MarkbookForGroupStudentsHeaderCell: FC = () => {
  const { formatMessage } = useIntl();

  return (
    <Stack direction="row" alignItems="center" pl={1}>
      <Typography color="text.secondary">{formatMessage({ id: 'people-Name' })}</Typography>
    </Stack>
  );
};

export type MarkbookForGroupsAssessmentHeaderCellProps = {
  assessment: Assessment;
  onMouseOver?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onMouseLeave?: () => void;
  assessmentDateVisible?: boolean;
};

export const MarkbookForGroupsAssessmentHeaderCell: FC<
  MarkbookForGroupsAssessmentHeaderCellProps
> = ({ assessment, onMouseOver, onMouseLeave, assessmentDateVisible }) => {
  return (
    <Stack
      justifyContent="center"
      sx={(theme) => ({
        width: '100%',
        height: '100%',
        px: theme.spacing(1),
        color: theme.palette.text.secondary,
      })}
      onMouseOver={onMouseOver}
      onMouseLeave={onMouseLeave}
    >
      <MarkbookAssessmentInfo
        assessmentDateVisible={assessmentDateVisible}
        assessment={assessment}
        linkProps={{ target: '_blank' }}
      />
    </Stack>
  );
};
export const MarkbookForGroupsAverageHeaderCell: FC = () => {
  const { formatMessage } = useIntl();

  return (
    <Stack
      sx={({ spacing }) => ({
        width: '100%',
        justifyContent: 'center',
        height: '100%',
        px: spacing(1),
        minHeight: spacing(7.75),
      })}
    >
      <Typography>{formatMessage({ id: 'assessments-markbook-studentsAverage' })}</Typography>
    </Stack>
  );
};

export type MarkbookForGroupsSubjectHeaderCellProps = {
  subject: MarkbookSubject;
};

export const MarkbookForGroupsSubjectHeaderCell: FC<MarkbookForGroupsSubjectHeaderCellProps> = ({
  subject,
}) => {
  const { formatMessage } = useIntl();

  const label = subject.labelId ? formatMessage({ id: subject.labelId }) : subject.name;

  if (!label) return null;

  return (
    <Box p={1} sx={{ cursor: 'default', overflow: 'hidden' }}>
      <Tooltip
        componentsProps={{ tooltip: { sx: { p: 1.25, maxWidth: 200 } } }}
        title={
          <Stack>
            {subject.groups.map((g) => (
              <Typography
                key={g.id}
                component={Link}
                to={`/groups/${g.id}`}
                target="_blank"
                sx={{ '&:hover': { textDecoration: 'underline', color: 'primary.main' } }}
              >
                {g.name}
              </Typography>
            ))}
          </Stack>
        }
      >
        <Stack>
          <TagSimple variant="outlined" sx={{ alignItems: undefined }}>
            <Typography noWrap>{label}</Typography>
          </TagSimple>
        </Stack>
      </Tooltip>
    </Box>
  );
};

// Moves column header cell within container width
// rely on students header cell position to calculate the new offset
// subscribes to scroll event of the .${ASSESSMENT_CSS_CLASSES.gridContainer} .MuiDataGrid-virtualScroller and updates the offset left
export const FixedHorizontalHeaderCell: FC<StackProps & { width: number; offsetLeft: number }> = ({
  sx,
  width,
  offsetLeft: containerOffsetLeft,
  ...rest
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [offsetLeft, setOffsetLeft] = useState(0);
  const styling = useMarkbookForGroupStyling();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const containerElement = document.querySelector<HTMLElement>(
      `.${ASSESSMENT_CSS_CLASSES.gridContainer} .MuiDataGrid-virtualScroller`,
    );
    if (!containerElement) return;

    const onScroll = debounce(() => {
      const { width: elWidth } = element.getBoundingClientRect();
      const maxOffset = width - elWidth;
      const newOffset = Math.min(maxOffset, containerElement.scrollLeft - containerOffsetLeft);

      setOffsetLeft(Math.max(0, newOffset));
      return;
    }, 300);

    containerElement?.addEventListener('scroll', onScroll);

    return () => {
      onScroll.clear();
      containerElement?.removeEventListener('scroll', onScroll);
    };
  }, [width, styling.studentNameCellWidth, containerOffsetLeft]);

  return (
    <Box
      ref={containerRef}
      sx={{
        ...sx,
        position: 'relative',
        transition: 'left 0.3s ease-out',
        left: offsetLeft,
        zIndex: 1,
      }}
      {...rest}
    />
  );
};

type MarkbookMethodTypeCellProps = {
  type: AssessmentMethodType;
  isCompared?: boolean;
  onToggleCompare?: () => void;
} & DataGridSortableCellProps<GetMarkbookSortBy>;

export const MarkbookMethodTypeCell: FC<MarkbookMethodTypeCellProps> = ({
  type,
  isSorted,
  isCompared,
  onToggleCompare,
  ...props
}) => {
  const { formatMessage } = useIntl();

  return (
    <>
      <DataGridSortableCell
        isSorted={isSorted}
        sx={{
          '& .compare-icon-container': {
            opacity: isCompared ? 1 : 0,
            transition: 'opacity 0.3s ease-out',
          },
          '&:hover': {
            '& .compare-icon-container': {
              opacity: 1,
            },
          },
          px: onToggleCompare ? 3.5 : 2.5,
        }}
        {...props}
      >
        <Typography
          noWrap
          sx={(theme) => ({
            color: isSorted || isCompared ? theme.palette.common.grey2 : theme.palette.common.grey,
          })}
        >
          {formatMessage({ id: `assessments-Type-${type}` })}
        </Typography>
        {onToggleCompare ? (
          <MarkbookCompareIcon isCompared={isCompared} onToggleCompare={onToggleCompare} />
        ) : undefined}
      </DataGridSortableCell>
    </>
  );
};

type MarkbookMultipleGroupsEntriesCellProps = {
  entries: MarkbookAssessmentEntry[];
  linkTo: LinkProps['to'];
};

export const MarkbookMultipleGroupsEntriesCell: FC<MarkbookMultipleGroupsEntriesCellProps> = ({
  entries,
  linkTo,
}) => {
  const { formatMessage } = useIntl();
  const count = entries.length;

  const getEntryResult = (entry: MarkbookAssessmentEntry) => {
    switch (entry.method.type) {
      case AssessmentMethodType.Score:
        return entry.score ?? '-';
      case AssessmentMethodType.Comment:
        return entry.comment ?? '-';
      case AssessmentMethodType.Grade:
        return entry.select_list_option_id ?? '-';
    }
  };

  return (
    <Stack alignItems="center" justifyContent="center" sx={{ cursor: 'default' }}>
      <Tooltip
        componentsProps={{ tooltip: { sx: { p: 1.25, maxWidth: 200 } } }}
        title={
          <Stack alignItems="flex-start" gap={1.25}>
            <Typography>{formatMessage({ id: 'assessments-markbook-multipleEntries' })}</Typography>

            <List
              sx={{
                listStyleType: 'disc',
                px: 2,
                py: 0,
                ml: 1,
                color: 'text.primary',
                maxWidth: '100%',
              }}
            >
              {entries.map((entry) => {
                const key = assessmentMethodTypes.isComment(entry.method.type)
                  ? `${entry.group.id}_${entry.creator?.relation_id}`
                  : entry.group.id;

                return (
                  <ListItem key={key} sx={{ display: 'list-item', p: 0 }}>
                    <Typography>{entry.group.name}</Typography>
                    <Typography noWrap>{getEntryResult(entry)}</Typography>
                  </ListItem>
                );
              })}
            </List>

            <Link to={linkTo} target="_blank">
              <SimpleButton
                sx={(theme) => ({
                  '& .MuiButton-startIcon .svg-icon': {
                    fontSize: theme.spacing(2),
                  },
                })}
                startIcon={<NewTabIcon />}
              >
                <Typography>
                  {formatMessage({ id: 'assessments-markbook-viewAndEditInProfile' })}
                </Typography>
              </SimpleButton>
            </Link>
          </Stack>
        }
      >
        <Counter
          inverse
          sx={{
            textAlign: 'center',
            px: 1,
            py: 0.5,
            borderRadius: 2,
            ml: 0,
            borderColor: 'common.light2',
          }}
        >
          <Typography variant="h3">{`x${count}`}</Typography>
        </Counter>
      </Tooltip>
    </Stack>
  );
};

type MarkbookStudentNameCellProps = {
  student: PersonCardBasicProps['user'];
  cellProps?: StackProps;
  linkProps?: Omit<LinkProps, 'to'>;
};

export const MarkbookStudentNameCell: FC<MarkbookStudentNameCellProps> = ({
  student,
  cellProps,
  linkProps,
}) => {
  const { sx, ...props } = cellProps ?? {};

  return (
    <Stack
      direction="row"
      gap={1}
      alignItems="center"
      sx={(theme) => ({
        overflow: 'hidden',
        ...((typeof sx === 'function' ? sx(theme) : sx) as SystemStyleObject),
      })}
      {...props}
    >
      <AvatarAuth user={student} withAvatarPreview />
      <TypographyWithOverflowHint
        variant="h3"
        noWrap
        component={Link}
        sx={{ color: 'text.primary', '&:hover': { color: 'inherit' } }}
        to={{ pathname: `/students/${student.relation_id}` }}
        {...linkProps}
      >
        {getUserFullName(student)}
      </TypographyWithOverflowHint>
    </Stack>
  );
};

export const MarkbookStudentNameSkeletonCell: FC<StackProps> = (props) => {
  return (
    <Stack direction="row" alignItems="center" gap={1} {...props}>
      <Skeleton variant="circular" width={30} height={30} />
      <Skeleton
        variant="rectangular"
        width="70%"
        height={22}
        sx={{ '&.MuiSkeleton-rectangular': { borderRadius: 0.5 } }}
      />
    </Stack>
  );
};

type MarkbookCategoryNameCellProps = {
  category: MarkbookCategory;
  totalStudents: number;
} & DataGridSortableCellProps<GetMarkbookSortBy>;

export const MarkbookCategoryNameCell: FC<MarkbookCategoryNameCellProps> = ({
  category,
  totalStudents,
  ...props
}) => {
  return (
    <DataGridSortableCell {...props}>
      <Box>
        <Stack flexDirection="row">
          <TypographyWithOverflowHint>{category.name}</TypographyWithOverflowHint>
          <Counter className="counter" inverse sx={{ minWidth: 20, textAlign: 'center', py: 0 }}>
            {totalStudents}
          </Counter>
        </Stack>
      </Box>
    </DataGridSortableCell>
  );
};

type MarkbookLoadMoreCellProps = {
  onClick: () => void;
  isLoading: boolean;
  disabled: boolean;
} & StackProps;

export const MarkbookLoadMoreCell: FC<MarkbookLoadMoreCellProps> = ({
  onClick,
  isLoading,
  disabled,
  ...props
}) => {
  return (
    <Stack {...props}>
      {isLoading ? (
        <Loading />
      ) : (
        <Button startIcon={<EyeIcon />} variant="text" onClick={onClick} disabled={disabled}>
          <FormattedMessage id="action-ShowMoreButton" />
        </Button>
      )}
    </Stack>
  );
};

export const AssessmentMarkbookEmptyCell = () => {
  return (
    <Stack
      width="100%"
      height="100%"
      sx={(theme) => ({
        backgroundImage: `repeating-linear-gradient(135deg, transparent ${theme.spacing(0, 1)}, ${
          theme.palette.common.light3
        }  ${theme.spacing(1, '9px')} )`,
      })}
    />
  );
};

export const AssessmentMarkbookDisabledCell: FC<{ backgroundColor?: string } & StackProps> = ({
  backgroundColor = 'background.default',
  ...props
}) => {
  return (
    <Stack
      width="100%"
      height="100%"
      sx={{
        backgroundColor,
      }}
      {...props}
    />
  );
};

export const AssessmentMarkbookDisabledMethodTypeCell: FC<
  { backgroundColor?: string; onToggleCompare?: () => void } & StackProps
> = ({ backgroundColor = 'background.paper', onToggleCompare, ...props }) => {
  const { formatMessage } = useIntl();

  return (
    <Stack
      position="relative"
      height="100%"
      width="100%"
      alignItems="center"
      justifyContent="center"
      {...props}
    >
      <Box overflow="hidden">
        <Typography color="common.grey">
          {formatMessage({ id: 'assessments-markbook-Baseline' })}
        </Typography>
      </Box>
      {onToggleCompare ? (
        <MarkbookCompareIcon isCompared inverse={true} onToggleCompare={onToggleCompare} />
      ) : undefined}
    </Stack>
  );
};

const MarkbookCompareIcon: FC<{
  isCompared?: boolean;
  inverse?: boolean;
  onToggleCompare: (e: React.MouseEvent<HTMLButtonElement>) => void;
}> = ({ isCompared, inverse, onToggleCompare }) => {
  const { formatMessage } = useIntl();

  const handleToggleCompare = (e: React.MouseEvent<HTMLButtonElement>) => {
    onToggleCompare?.(e);
    e.stopPropagation();
  };

  return (
    <Tooltip
      title={
        <Stack gap={1} alignItems="flex-start">
          <Typography>
            {isCompared
              ? formatMessage({ id: 'assessments-markbook-UsedAsBaselineDescription' })
              : formatMessage({ id: 'assessments-markbook-SetAsBaseline' })}
          </Typography>
          <SimpleButton
            size="small"
            startIcon={isCompared ? <CloseIcon /> : <CompareIcon />}
            onClick={handleToggleCompare}
          >
            {isCompared
              ? formatMessage({ id: 'assessments-markbook-FinishComparison' })
              : formatMessage({ id: 'assessments-markbook-Compare' })}
          </SimpleButton>
        </Stack>
      }
    >
      <Box
        className="compare-icon-container"
        sx={(theme) => ({
          position: 'absolute',
          alignSelf: 'flex-end',
          left: theme.spacing(0.5),
        })}
      >
        <IconButton inverse={inverse ?? !isCompared} onClick={handleToggleCompare}>
          <CompareIcon />
        </IconButton>
      </Box>
    </Tooltip>
  );
};

type MarkbookAverageCategoryCellProps = {
  isDisabled?: boolean;
  isCompared?: boolean;
  onToggleCompare?: () => void;
};

export const MarkbookAverageCategoryCell: FC<MarkbookAverageCategoryCellProps> = ({
  isDisabled,
  isCompared,
  onToggleCompare,
}) => {
  const { formatMessage } = useIntl();
  return (
    <Stack
      position="relative"
      height="100%"
      width="100%"
      alignItems="center"
      justifyContent="center"
      sx={{
        '& .compare-icon-container': {
          opacity: isCompared ? 1 : 0,
          transition: 'opacity 0.3s ease-out',
        },
        '&:hover': {
          '& .compare-icon-container': {
            opacity: 1,
          },
        },
        px: onToggleCompare ? 3.5 : 2.5,
      }}
    >
      <Box overflow="hidden">
        <Typography color={isDisabled || !isCompared ? 'common.grey' : 'primary.main'}>
          {isDisabled ? formatMessage({ id: 'assessments-markbook-Baseline' }) : '%'}
        </Typography>
      </Box>
      {onToggleCompare ? (
        <MarkbookCompareIcon
          isCompared={isCompared}
          inverse={isDisabled || !isCompared}
          onToggleCompare={onToggleCompare}
        />
      ) : undefined}
    </Stack>
  );
};
